
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useNotificationAnalytics } from "@/hooks/useNotificationAnalytics";
import { useState } from "react";
import NotificationSummaryCards from "./NotificationSummaryCards";
import NotificationTrendsChart from "./NotificationTrendsChart";
import NotificationTypeChart from "./NotificationTypeChart";
import NotificationTypeTable from "./NotificationTypeTable";
import EmailLogsTable from "./EmailLogsTable";

const NotificationAnalyticsDashboard = () => {
  const { analytics, emailLogs, isLoading, fetchAnalytics, getAnalyticsSummary, getNotificationTypeBreakdown } = useNotificationAnalytics();
  const [timeRange, setTimeRange] = useState("30");

  const summary = getAnalyticsSummary();
  const typeBreakdown = getNotificationTypeBreakdown();

  const chartData = analytics.slice(0, 14).reverse().map(item => ({
    date: new Date(item.date).toLocaleDateString(),
    sent: item.total_sent,
    delivered: item.total_delivered,
    failed: item.total_failed,
    read: item.total_read,
  }));

  const pieData = typeBreakdown.map(item => ({
    name: item.type.replace('_', ' '),
    value: item.sent,
  }));

  const handleTimeRangeChange = (days: string) => {
    setTimeRange(days);
    fetchAnalytics(parseInt(days));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Notification Analytics</h2>
          <p className="text-muted-foreground">Track notification performance and delivery metrics</p>
        </div>
        <Select value={timeRange} onValueChange={handleTimeRangeChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <NotificationSummaryCards summary={summary} />

      {/* Charts and Tables */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="types">By Type</TabsTrigger>
          <TabsTrigger value="emails">Email Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <NotificationTrendsChart chartData={chartData} />
        </TabsContent>

        <TabsContent value="types" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NotificationTypeChart pieData={pieData} />
            <NotificationTypeTable typeBreakdown={typeBreakdown} />
          </div>
        </TabsContent>

        <TabsContent value="emails" className="space-y-6">
          <EmailLogsTable emailLogs={emailLogs} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationAnalyticsDashboard;
